import React, {lazy, Suspense} from 'react';
import ReactDOM from 'react-dom';
import ReactModal from 'react-modal';
import {FormattedMessage} from 'react-intl';

import LabcodeAuthGate from '../components/labcode-auth-gate/labcode-auth-gate.jsx';
import log from '../lib/log.js';
import MessageBoxType from '../lib/message-box.js';

import GuiLoadingFallback from './gui-loading-fallback.jsx';

const GuiPlaygroundChunk = lazy(() => import(
    /* webpackChunkName: "gui-playground" */ './gui-playground-chunk.jsx'
));

const onClickLogo = () => {
    window.location = 'https://www.labcode.com.br/';
};

const onClickCheckUpdate = () => {
    log('User click check update');
};

const onClickUpdate = () => {
    log('User click update');
};

const onAbortUpdate = () => {
    log('User click abort update');
};

const onClickClearCache = () => {
    log('User click clear cahce');
};

const onClickInstallDriver = () => {
    log('User click install driver');
};

const handleTelemetryModalCancel = () => {
    log('User canceled telemetry modal');
};

const handleTelemetryModalOptIn = () => {
    log('User opted into telemetry');
};

const handleTelemetryModalOptOut = () => {
    log('User opted out of telemetry');
};

const onClickAbout = [
    {
        title: (<FormattedMessage
            defaultMessage="About"
            description="Menu bar item for about"
            id="gui.desktopMenuBar.about"
        />),
        onClick: () => log('About')
    },
    {
        title: (<FormattedMessage
            defaultMessage="License"
            description="Menu bar item for license"
            id="gui.desktopMenuBar.license"
        />),
        onClick: () => log('License')
    },
    {
        title: (<FormattedMessage
            defaultMessage="Privacy policy"
            description="Menu bar item for privacy policy"
            id="gui.menuBar.privacyPolicy"
        />),
        onClick: () => log('Privacy Policy')
    },
    {
        title: (<FormattedMessage
            defaultMessage="Data settings"
            description="Menu bar item for data settings"
            id="gui.menuBar.dataSettings"
        />),
        onClick: () => log('Data Settings')
    }
];

const handleShowMessageBox = (type, message) => {
    if (type === MessageBoxType.confirm) {
        return confirm(message); // eslint-disable-line no-alert
    } else if (type === MessageBoxType.alert) {
        return alert(message); // eslint-disable-line no-alert
    }
};

/**
 * O editor principal pede-se só aqui (import dinâmico): no portão full, o bundle grande
 * só desce da rede depois do login. Sem portão, há um carregamento breve na primeira ida.
 */
const editorTree = chunkProps => (
    <Suspense fallback={<GuiLoadingFallback />}>
        <GuiPlaygroundChunk {...chunkProps} />
    </Suspense>
);

/*
 * Render the GUI playground. This is a separate function because importing anything
 * that instantiates the VM causes unsupported browsers to crash
 * {object} appTarget - the DOM element to render to
 */
export default appTarget => {
    ReactModal.setAppElement(appTarget);

    const backpackHostMatches = window.location.href.match(/[?&]backpack_host=([^&]*)&?/);
    const backpackHost = backpackHostMatches ? backpackHostMatches[1] : null;

    const scratchDesktopMatches = window.location.href.match(/[?&]isScratchDesktop=([^&]+)/);
    let simulateScratchDesktop;
    if (scratchDesktopMatches) {
        try {
            simulateScratchDesktop = JSON.parse(scratchDesktopMatches[1]);
        } catch {
            simulateScratchDesktop = scratchDesktopMatches[1];
        }
    }

    const chunkProps = {
        simulateScratchDesktop: !!simulateScratchDesktop,
        backpackHost,
        onClickAbout,
        onClickLogo,
        handleTelemetryModalCancel,
        handleTelemetryModalOptIn,
        handleTelemetryModalOptOut,
        onAbortUpdate,
        onClickCheckUpdate,
        onClickUpdate,
        onClickClearCache,
        onClickInstallDriver,
        handleShowMessageBox
    };

    if (process.env.NODE_ENV === 'production' && typeof window === 'object') {
        window.onbeforeunload = () => true;
    }

    try {
        ReactDOM.render(
            <LabcodeAuthGate>
                {editorTree(chunkProps)}
            </LabcodeAuthGate>,
            appTarget);
    } catch (err) {
        if (typeof console !== 'undefined' && console.error) {
            console.error('[Labcode] Falha ao iniciar a interface:', err);
        }
        if (appTarget) {
            appTarget.textContent = '';
            const p = document.createElement('p');
            p.style.cssText = 'padding:1.5rem;font-family:sans-serif;max-width:40rem;';
            p.textContent =
                'Não foi possível iniciar o Labcode. Abra a consola do browser (F12) para ver o erro, ' +
                'confirme que npm start terminou de compilar e recarregue a página.';
            appTarget.appendChild(p);
        }
    } finally {
        const splash = typeof document !== 'undefined' && document.getElementById('pre-loading');
        if (splash && splash.parentNode) {
            splash.parentNode.removeChild(splash);
        }
    }
};
